import type {
  IngridCompleteSessionResponse,
  Option,
  OrderDTO,
  PaymentProviderConfig,
  ShippingProviderConfig,
  ShippingProviderName,
  SveaOrder
} from 'ecosystem'
import { getStorefrontOrderByOrderNumber } from '../getStorefrontOrderByOrderNumber'
import { sveaApi } from '../../../utils/svea'
import { createError } from '../../../utils/fetching'
import { completeStorefrontOrder } from './completeStorfrontOrder'
import type { OrderProvisionResult } from './types'
import {
  generateIngridShippingProviderResult,
  generatePostnordShippingProviderResult,
  generateSveaAddresses
} from './utils'

export interface ProvisionOrderSveaProviderResult {
  sveaResponse: SveaOrder
  storefrontOrder: Option<OrderDTO>
  shippingProviderResult?: {
    providerName: ShippingProviderName
    ingridResponse?: IngridCompleteSessionResponse['session']
  }
}

export const provisionOrderSveaProvider = async (
  orderId: string,
  paymentProviderConfig?: PaymentProviderConfig,
  shippingProviderConfig?: ShippingProviderConfig
): Promise<Option<ProvisionOrderSveaProviderResult>> => {
  // 1. Confirmation in Svea
  const { error: sveaError, data: sveaOrderResponse } =
    await sveaApi.checkout.retrieveOrder(orderId)

  if (sveaError || !sveaOrderResponse) {
    throw Error(sveaError || 'Svea GET endpoint error')
  }

  if (sveaOrderResponse.Status !== 'Final') {
    throw Error('Svea order is not finalized yet')
  }

  // 2. Checks if the order exists
  const { error: getOrderError, data: orderData } = await getStorefrontOrderByOrderNumber({
    orderNumber: sveaOrderResponse.ClientOrderNumber
  })

  // 3. If the order doesn't exist, returns an error
  if (getOrderError || !orderData) {
    throw createError(getOrderError ?? 'BE GET endpoint error')
  }

  const getShippingProviderResult = async (): Promise<
    OrderProvisionResult['shippingProviderResult'] | void
  > => {
    if (!orderData?.shippingProvider) {
      return
    }

    switch (orderData?.shippingProvider) {
      case 'INGRID':
        return generateIngridShippingProviderResult(orderData)
      case 'POSTNORD': {
        const { customerInfo } = generateSveaAddresses(sveaOrderResponse)

        return generatePostnordShippingProviderResult(
          orderData,
          {
            phoneNo: customerInfo.phone,
            smsNo: customerInfo.phone,
            emailAddress: customerInfo.email,
            contactName: customerInfo.fullName
          },
          shippingProviderConfig
        )
      }
      case 'DEFAULT':
    }
  }

  // 4. Get shipping provider information
  const shippingProviderResult = await getShippingProviderResult()

  // 5. Complete storefront order, change status to PAID and update all info
  const storefrontOrder = await completeStorefrontOrder({
    order: orderData,
    paymentProviderInfo: {
      ...paymentProviderConfig,
      providerName: 'SVEA',
      providerResponse: {
        sveaResponse: sveaOrderResponse
      }
    },
    ...(shippingProviderResult && {
      shippingProviderInfo: shippingProviderResult
    })
  })

  return {
    storefrontOrder,
    sveaResponse: sveaOrderResponse,
    ...(shippingProviderResult && {
      shippingProviderResult
    })
  }
}
